import { useState, useEffect } from 'react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Shield, Users, DollarSign, TrendingUp, Settings, Mail, 
  Download, Eye, RefreshCw, Clock, CheckCircle2, XCircle,
  Send, Gift, AlertCircle, Activity, CreditCard, Calendar,
  ShieldCheck, Loader2
} from 'lucide-react';

export default function AdminSoldierPortal({ token }) {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [showBonus, setShowBonus] = useState(false);
  const [selectedSoldier, setSelectedSoldier] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: '', body: '', recipient_type: 'all' });
  const [bonusForm, setBonusForm] = useState({ soldier_id: '', amount: 0, reason: '', adjustment_type: 'bonus' });
  const [savingSettings, setSavingSettings] = useState(false);
  const [overriding, setOverriding] = useState(null); // Track which user is being overridden

  useEffect(() => {
    fetchDashboard();
  }, [token]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      // Use admin-specific endpoints for the admin portal
      const [statsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/soldiers/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/soldiers/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      // Transform admin stats to match expected dashboard format
      setDashboard({
        stats: statsRes.data,
        soldier_id: 'admin',
        salon_name: 'Admin View',
        referral_code: 'ADMIN',
        rank: 'super_admin'
      });
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to load soldier portal:', error);
      // Set default values on error
      setDashboard({
        stats: {
          total_soldiers: 0,
          active_soldiers: 0,
          pending_applications: 0,
          total_referrals: 0,
          total_payouts: 0,
          pending_payouts: 0
        },
        soldier_id: 'admin',
        salon_name: 'Admin View',
        referral_code: 'ADMIN',
        rank: 'super_admin'
      });
      setSettings({
        base_commission_percent: 5,
        super_soldier_bonus_percent: 2,
        payout_minimum: 50
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/soldiers/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved successfully');
      setShowSettings(false);
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSendEmail = async () => {
    try {
      const res = await axios.post(`${API}/soldiers/admin/portal/email`, emailForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Sent to ${res.data.sent} soldiers`);
      setShowEmail(false);
      setEmailForm({ subject: '', body: '', recipient_type: 'all' });
    } catch (error) {
      // Endpoint may not exist yet - show friendly message
      if (error.response?.status === 404) {
        toast.info('Email feature coming soon');
      } else {
        toast.error('Failed to send emails');
      }
      setShowEmail(false);
    }
  };

  const handleAddBonus = async () => {
    try {
      await axios.post(`${API}/soldiers/admin/portal/bonus`, bonusForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bonus adjustment applied');
      setShowBonus(false);
      setBonusForm({ soldier_id: '', amount: 0, reason: '', adjustment_type: 'bonus' });
      fetchDashboard();
    } catch (error) {
      // Endpoint may not exist yet - show friendly message
      if (error.response?.status === 404) {
        toast.info('Bonus feature coming soon');
      } else {
        toast.error('Failed to apply adjustment');
      }
      setShowBonus(false);
    }
  };

  const handleExport = async (type) => {
    try {
      // Try to use existing export functionality or show coming soon
      const res = await axios.get(`${API}/soldiers/admin/referrals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert to CSV
      const data = res.data.referrals || [];
      if (data.length === 0) {
        toast.error('No data to export');
        return;
      }
      
      const headers = Object.keys(data[0]);
      const csv = [
        headers.join(','),
        ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
      ].join('\n');
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `soldiers_${type}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      
      toast.success(`Exported ${data.length} ${type}`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  // Override certification and activate soldier
  const handleOverrideCertification = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to override certification requirements and activate ${userName} as a soldier?\n\nThis will bypass the Soldier Academy requirement.`)) {
      return;
    }

    setOverriding(userId);
    try {
      const response = await axios.post(
        `${API}/soldiers/admin/users/${userId}/override-certification`,
        { reason: "Admin override - bypassing Academy requirement" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`${userName} has been activated as a soldier!`);
        fetchDashboard(); // Refresh the dashboard
      }
    } catch (error) {
      console.error('Override failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to override certification');
    } finally {
      setOverriding(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-amber-400 animate-spin" />
      </div>
    );
  }

  const overview = dashboard?.stats || {};
  const soldiers = dashboard?.stats?.soldiers || [];
  const pendingCertification = dashboard?.stats?.pending_certification || [];
  const recentActivity = dashboard?.recent_activity || [];

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-amber-400" />
            Soldier Command Center
          </h2>
          <p className="text-gray-400 text-sm">Manage affiliates, commissions, and payouts</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a 
            href="/docs/soldier-program-guide.html" 
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium h-9 px-3 border border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
          >
            <Download className="w-4 h-4" />
            Program Guide
          </a>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => setShowSettings(true)}
            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
          >
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => setShowEmail(true)}
            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
          >
            <Mail className="w-4 h-4 mr-2" />
            Email Soldiers
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => setShowBonus(true)}
            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
          >
            <Gift className="w-4 h-4 mr-2" />
            Add Bonus
          </Button>
          <Button size="sm" onClick={fetchDashboard} variant="outline" className="border-stone-700">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-amber-500/20 to-yellow-500/10 border-amber-500/30">
          <CardContent className="p-4 text-center">
            <Shield className="w-6 h-6 text-amber-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{overview?.total_soldiers || soldiers?.length || 0}</p>
            <p className="text-xs text-amber-400">Total Soldiers</p>
          </CardContent>
        </Card>
        <Card className="bg-stone-900 border-stone-800">
          <CardContent className="p-4 text-center">
            <CheckCircle2 className="w-6 h-6 text-green-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{overview?.active_soldiers || 0}</p>
            <p className="text-xs text-gray-400">Active</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500/20 to-amber-500/10 border-orange-500/30">
          <CardContent className="p-4 text-center">
            <Clock className="w-6 h-6 text-orange-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{overview?.pending_certification_count || pendingCertification?.length || 0}</p>
            <p className="text-xs text-orange-400">Pending Certification</p>
          </CardContent>
        </Card>
        <Card className="bg-stone-900 border-stone-800">
          <CardContent className="p-4 text-center">
            <Users className="w-6 h-6 text-blue-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{overview?.referrals?.total || 0}</p>
            <p className="text-xs text-gray-400">Total Referrals</p>
          </CardContent>
        </Card>
        <Card className="bg-stone-900 border-stone-800">
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-6 h-6 text-purple-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{overview?.referrals?.active || 0}</p>
            <p className="text-xs text-gray-400">Active Referrals</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/10 border-green-500/30">
          <CardContent className="p-4 text-center">
            <DollarSign className="w-6 h-6 text-green-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">${(overview?.payouts?.total_paid || 0).toFixed(2)}</p>
            <p className="text-xs text-green-400">Total Paid</p>
          </CardContent>
        </Card>
        <Card className="bg-stone-900 border-stone-800">
          <CardContent className="p-4 text-center">
            <CreditCard className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">${(overview?.payouts?.total_pending || 0).toFixed(2)}</p>
            <p className="text-xs text-gray-400">Pending Payout</p>
          </CardContent>
        </Card>
      </div>

      {/* Current Settings Display */}
      <Card className="bg-stone-900/50 border-stone-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <Settings className="w-5 h-5 text-amber-400" />
            Commission Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 text-sm">
            <div className="bg-stone-800/50 rounded-lg p-3">
              <p className="text-gray-400 text-xs mb-1">Pro M1 Bonus</p>
              <p className="text-white font-bold">${settings?.pro_month1_bonus || 10}</p>
            </div>
            <div className="bg-stone-800/50 rounded-lg p-3">
              <p className="text-gray-400 text-xs mb-1">Pro M2 Bonus</p>
              <p className="text-white font-bold">${settings?.pro_month2_bonus || 10}</p>
            </div>
            <div className="bg-stone-800/50 rounded-lg p-3">
              <p className="text-gray-400 text-xs mb-1">Elite M1 Bonus</p>
              <p className="text-white font-bold">${settings?.elite_month1_bonus || 15}</p>
            </div>
            <div className="bg-stone-800/50 rounded-lg p-3">
              <p className="text-gray-400 text-xs mb-1">Elite M2 Bonus</p>
              <p className="text-white font-bold">${settings?.elite_month2_bonus || 15}</p>
            </div>
            <div className="bg-stone-800/50 rounded-lg p-3">
              <p className="text-gray-400 text-xs mb-1">Lifetime %</p>
              <p className="text-white font-bold">{settings?.lifetime_commission_percent || 8}%</p>
            </div>
            <div className="bg-stone-800/50 rounded-lg p-3">
              <p className="text-gray-400 text-xs mb-1">Payout Schedule</p>
              <p className="text-white font-bold capitalize">{settings?.payout_frequency || 'weekly'} ({settings?.payout_day || 'friday'})</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Soldiers Table & Activity Side by Side */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Soldiers Table */}
        <Card className="lg:col-span-2 bg-stone-900 border-stone-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-400" />
                Soldier Leaderboard
              </CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleExport('soldiers')} className="border-stone-700 text-gray-400">
                  <Download className="w-4 h-4 mr-1" /> Export
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-800">
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Soldier</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Referrals</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Earned</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Paid</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Status</th>
                    <th className="text-right py-3 px-2 text-gray-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {soldiers.slice(0, 15).map((soldier, idx) => (
                    <tr key={soldier.user_id} className="border-b border-stone-800/50 hover:bg-stone-800/30">
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          {idx < 3 && (
                            <span className={`text-lg ${idx === 0 ? 'text-yellow-400' : idx === 1 ? 'text-gray-300' : 'text-amber-600'}`}>
                              {idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}
                            </span>
                          )}
                          <div>
                            <p className="text-white font-medium">{soldier.name}</p>
                            <p className="text-gray-500 text-xs">{soldier.salon_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <span className="text-white">{soldier.total_referrals}</span>
                        <span className="text-gray-500 text-xs ml-1">({soldier.active_referrals} active)</span>
                      </td>
                      <td className="py-3 px-2 text-green-400 font-medium">${(soldier.total_earned || 0).toFixed(2)}</td>
                      <td className="py-3 px-2 text-gray-300">${(soldier.total_paid || 0).toFixed(2)}</td>
                      <td className="py-3 px-2">
                        <Badge className={
                          soldier.status === 'active' ? 'bg-green-500/20 text-green-400' :
                          soldier.status === 'suspended' ? 'bg-red-500/20 text-red-400' :
                          'bg-gray-500/20 text-gray-400'
                        }>
                          {soldier.status}
                        </Badge>
                        {soldier.stripe_connected && (
                          <Badge className="ml-1 bg-blue-500/20 text-blue-400">💳</Badge>
                        )}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => {
                            setBonusForm({ ...bonusForm, soldier_id: soldier.user_id });
                            setShowBonus(true);
                          }}
                          className="text-green-400 hover:text-green-300"
                        >
                          <Gift className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Pending Certification Section */}
        {pendingCertification.length > 0 && (
          <Card className="bg-gradient-to-br from-orange-500/10 to-amber-500/5 border-orange-500/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-400" />
                Pending Certification ({pendingCertification.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400 text-sm mb-4">
                These users have been made soldiers but haven&apos;t completed the Soldier Academy yet.
                You can override this requirement and activate them directly.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-stone-700">
                      <th className="text-left py-2 px-3 text-gray-400 text-xs font-medium">Name</th>
                      <th className="text-left py-2 px-3 text-gray-400 text-xs font-medium">Email</th>
                      <th className="text-left py-2 px-3 text-gray-400 text-xs font-medium">Added</th>
                      <th className="text-center py-2 px-3 text-gray-400 text-xs font-medium">Status</th>
                      <th className="text-center py-2 px-3 text-gray-400 text-xs font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingCertification.map((user, idx) => (
                      <tr key={user.user_id || idx} className="border-b border-stone-800 hover:bg-stone-800/50">
                        <td className="py-3 px-3 text-white">{user.name}</td>
                        <td className="py-3 px-3 text-gray-400 text-sm">{user.email}</td>
                        <td className="py-3 px-3 text-gray-400 text-sm">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="py-3 px-3 text-center">
                          <Badge className="bg-orange-500/20 text-orange-400">
                            Awaiting Academy
                          </Badge>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <Button
                            size="sm"
                            onClick={() => handleOverrideCertification(user.user_id, user.name)}
                            disabled={overriding === user.user_id}
                            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-xs h-8"
                            data-testid={`override-btn-${user.user_id}`}
                          >
                            {overriding === user.user_id ? (
                              <>
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                Activating...
                              </>
                            ) : (
                              <>
                                <ShieldCheck className="w-3 h-3 mr-1" />
                                Override & Approve
                              </>
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Activity */}
        <Card className="bg-stone-900 border-stone-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-400" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {recentActivity.slice(0, 15).map((activity, idx) => (
                <div key={idx} className="flex items-start gap-3 p-2 rounded-lg hover:bg-stone-800/50">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    activity.type === 'referral' ? 'bg-blue-500/20' : 'bg-green-500/20'
                  }`}>
                    {activity.type === 'referral' ? (
                      <Users className="w-4 h-4 text-blue-400" />
                    ) : (
                      <DollarSign className="w-4 h-4 text-green-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">{activity.soldier_name}</p>
                    <p className="text-gray-400 text-xs truncate">{activity.details}</p>
                  </div>
                  <Badge className={`text-xs ${
                    activity.status === 'active' ? 'bg-green-500/20 text-green-400' :
                    activity.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {activity.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Export Options */}
      <Card className="bg-stone-900 border-stone-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Download className="w-5 h-5 text-cyan-400" />
            Export Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => handleExport('soldiers')} variant="outline" className="border-stone-700">
              <Download className="w-4 h-4 mr-2" /> Soldiers List
            </Button>
            <Button onClick={() => handleExport('payouts')} variant="outline" className="border-stone-700">
              <Download className="w-4 h-4 mr-2" /> Payout History
            </Button>
            <Button onClick={() => handleExport('referrals')} variant="outline" className="border-stone-700">
              <Download className="w-4 h-4 mr-2" /> All Referrals
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-stone-900 border-stone-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-amber-400" />
              Commission Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Pro M1 Bonus ($)</Label>
                <Input 
                  type="number" 
                  value={settings?.pro_month1_bonus || 10}
                  onChange={(e) => setSettings({ ...settings, pro_month1_bonus: parseFloat(e.target.value) })}
                  className="bg-stone-800 border-stone-700 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-400">Pro M2 Bonus ($)</Label>
                <Input 
                  type="number" 
                  value={settings?.pro_month2_bonus || 10}
                  onChange={(e) => setSettings({ ...settings, pro_month2_bonus: parseFloat(e.target.value) })}
                  className="bg-stone-800 border-stone-700 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-400">Elite M1 Bonus ($)</Label>
                <Input 
                  type="number" 
                  value={settings?.elite_month1_bonus || 15}
                  onChange={(e) => setSettings({ ...settings, elite_month1_bonus: parseFloat(e.target.value) })}
                  className="bg-stone-800 border-stone-700 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-400">Elite M2 Bonus ($)</Label>
                <Input 
                  type="number" 
                  value={settings?.elite_month2_bonus || 15}
                  onChange={(e) => setSettings({ ...settings, elite_month2_bonus: parseFloat(e.target.value) })}
                  className="bg-stone-800 border-stone-700 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-400">Lifetime Commission (%)</Label>
                <Input 
                  type="number" 
                  value={settings?.lifetime_commission_percent || 8}
                  onChange={(e) => setSettings({ ...settings, lifetime_commission_percent: parseFloat(e.target.value) })}
                  className="bg-stone-800 border-stone-700 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-400">Minimum Payout ($)</Label>
                <Input 
                  type="number" 
                  value={settings?.minimum_payout || 10}
                  onChange={(e) => setSettings({ ...settings, minimum_payout: parseFloat(e.target.value) })}
                  className="bg-stone-800 border-stone-700 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Payout Frequency</Label>
                <Select 
                  value={settings?.payout_frequency || 'weekly'}
                  onValueChange={(v) => setSettings({ ...settings, payout_frequency: v })}
                >
                  <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-700">
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-400">Payout Day</Label>
                <Select 
                  value={settings?.payout_day || 'friday'}
                  onValueChange={(v) => setSettings({ ...settings, payout_day: v })}
                >
                  <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-700">
                    <SelectItem value="monday">Monday</SelectItem>
                    <SelectItem value="tuesday">Tuesday</SelectItem>
                    <SelectItem value="wednesday">Wednesday</SelectItem>
                    <SelectItem value="thursday">Thursday</SelectItem>
                    <SelectItem value="friday">Friday</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowSettings(false)} className="border-stone-700">
                Cancel
              </Button>
              <Button 
                onClick={handleSaveSettings} 
                disabled={savingSettings}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                {savingSettings ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={showEmail} onOpenChange={setShowEmail}>
        <DialogContent className="bg-stone-900 border-stone-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-400" />
              Email Soldiers
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-400">Recipients</Label>
              <Select 
                value={emailForm.recipient_type}
                onValueChange={(v) => setEmailForm({ ...emailForm, recipient_type: v })}
              >
                <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-stone-800 border-stone-700">
                  <SelectItem value="all">All Soldiers</SelectItem>
                  <SelectItem value="active">Active Soldiers Only</SelectItem>
                  <SelectItem value="pending">Pending Applications</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-400">Subject</Label>
              <Input 
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                placeholder="Email subject..."
                className="bg-stone-800 border-stone-700 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-400">Message</Label>
              <Textarea 
                value={emailForm.body}
                onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                placeholder="Write your message..."
                rows={6}
                className="bg-stone-800 border-stone-700 text-white"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowEmail(false)} className="border-stone-700">
                Cancel
              </Button>
              <Button 
                onClick={handleSendEmail}
                disabled={!emailForm.subject || !emailForm.body}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                <Send className="w-4 h-4 mr-2" /> Send Email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bonus Adjustment Dialog */}
      <Dialog open={showBonus} onOpenChange={setShowBonus}>
        <DialogContent className="bg-stone-900 border-stone-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Gift className="w-5 h-5 text-green-400" />
              Manual Bonus Adjustment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-400">Select Soldier</Label>
              <Select 
                value={bonusForm.soldier_id}
                onValueChange={(v) => setBonusForm({ ...bonusForm, soldier_id: v })}
              >
                <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                  <SelectValue placeholder="Select a soldier..." />
                </SelectTrigger>
                <SelectContent className="bg-stone-800 border-stone-700 max-h-60">
                  {soldiers.map(s => (
                    <SelectItem key={s.user_id} value={s.user_id}>
                      {s.name} ({s.salon_name})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Amount ($)</Label>
                <Input 
                  type="number" 
                  value={bonusForm.amount}
                  onChange={(e) => setBonusForm({ ...bonusForm, amount: parseFloat(e.target.value) || 0 })}
                  className="bg-stone-800 border-stone-700 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-400">Type</Label>
                <Select 
                  value={bonusForm.adjustment_type}
                  onValueChange={(v) => setBonusForm({ ...bonusForm, adjustment_type: v })}
                >
                  <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-700">
                    <SelectItem value="bonus">Bonus</SelectItem>
                    <SelectItem value="deduction">Deduction</SelectItem>
                    <SelectItem value="correction">Correction</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-gray-400">Reason</Label>
              <Textarea 
                value={bonusForm.reason}
                onChange={(e) => setBonusForm({ ...bonusForm, reason: e.target.value })}
                placeholder="Reason for adjustment..."
                rows={3}
                className="bg-stone-800 border-stone-700 text-white"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowBonus(false)} className="border-stone-700">
                Cancel
              </Button>
              <Button 
                onClick={handleAddBonus}
                disabled={!bonusForm.soldier_id || !bonusForm.amount || !bonusForm.reason}
                className="bg-green-500 hover:bg-green-600 text-black"
              >
                Apply Adjustment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
